import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { useQueue, useQueues, type Department } from '../api/taskQueries.ts';
import { TaskRow } from '../components/tasks/TaskRow.tsx';
import { TaskCard } from '../components/tasks/TaskCard.tsx';
import { NewTaskSheet } from '../components/tasks/NewTask.tsx';
import { chip, plain } from '../components/tasks/Fields.tsx';
import { agencyToday } from '../components/tasks/FollowUp.tsx';
import { useQueueDeaths } from '../api/triage.ts';
import { DeathsNoDate } from './Triage.tsx';
import { showsLegalDeaths } from '../tasks/legalDeaths.ts';

/**
 * The department queue (147, ruling 6): each department's unassigned open work, for its owner role (Social Work:
 * DIR_SOCIAL_WORK; Legal: DIR_LEGAL; Compliance: DIR_COMPLIANCE; Property and Finance: their new directors), the
 * Executive Case Reviewer and tiers 1-2. Which queues the reader may open is the server's `may_open`. An unowned queue
 * is shown to tiers 1-2 and says so: never silently empty. A task here is nobody's yet, so opening it records nothing
 * as seen on anyone's behalf.
 */
export function DepartmentQueue() {
  const queues = useQueues();
  const mine = (queues.data?.queues ?? []).filter((q) => q.may_open);
  const [chosen, setChosen] = useState<Department | null>(null);
  const department = chosen ?? mine[0]?.department ?? null;
  const queue = useQueue(department);
  const meta = mine.find((q) => q.department === department);
  const [open, setOpen] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const today = agencyToday();
  // D-127: the deaths with no date recorded, for Legal (the server's shown); on the Legal tab, or for a reader who opens
  // no Legal queue (the paralegal), never on another department's tab.
  const deaths = useQueueDeaths();
  const drawDeaths = showsLegalDeaths(deaths.data, department, mine.map((q) => q.department));

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Department queue</h1>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/tasks" className={`${plain} inline-flex items-center`}>My tasks</Link>
            <Link to="/tasks/triage" className={`${plain} inline-flex items-center`}>Triage</Link>
            <button type="button" className={`${plain} ml-auto`} onClick={() => setMaking(true)}>New task</button>
          </div>
          {mine.length > 1 && (
            <div role="tablist" aria-label="Department" className="mt-2 flex flex-wrap gap-2">
              {mine.map((q) => (
                <button key={q.department} type="button" role="tab" aria-selected={q.department === department}
                        className={chip(q.department === department)} onClick={() => setChosen(q.department)}>
                  {q.department} <span className="tabular-nums">{q.open}</span>
                </button>
              ))}
            </div>
          )}
        </header>
        <div className="page-body">
          {queues.error && !queues.data ? (
            <LoadFailure what="The queues" error={queues.error} onRetry={() => void queues.refetch()} retrying={queues.isFetching} />
          ) : queues.data && mine.length === 0 ? (
            <p className="px-4 py-10 text-center text-neutral-700">No department queue is yours to open.</p>
          ) : (
            <>
              {meta && meta.owner_role === null && (
                <p role="status" className="mx-4 mt-3 rounded-lg border-l-8 border-note-edge bg-note-bg px-4 py-3 font-semibold text-note-fg">
                  {meta.department} has no queue owner yet, so its queue is shown to tiers 1 and 2.
                </p>
              )}
              {queue.error && !queue.data ? (
                <LoadFailure what="This queue" error={queue.error} onRetry={() => void queue.refetch()} retrying={queue.isFetching} />
              ) : !queue.data ? (
                <p className="px-4 py-6 text-neutral-700">Loading…</p>
              ) : queue.data.tasks.length === 0 ? (
                <p className="px-4 py-10 text-center text-neutral-700">Nothing is waiting in the {queue.data.department} queue.</p>
              ) : (
                <ol className="divide-y divide-neutral-200">
                  {queue.data.tasks.map((t) => <TaskRow key={t.id} task={t} today={today} onOpen={setOpen} showHolder />)}
                </ol>
              )}
            </>
          )}
          {drawDeaths && deaths.data && (
            <div className="px-4 pb-4 lg:px-5"><DeathsNoDate count={deaths.data.count} rows={deaths.data.rows} /></div>
          )}
        </div>
      </div>
      {open && <TaskCard taskId={open} onClose={() => setOpen(null)} />}
      {making && <NewTaskSheet onClose={() => setMaking(false)} />}
    </div>
  );
}
